import { escapeHtml } from "../../ui/escapeHtml.js";

export interface BossEntryConfirmation {
  accessId: string;
  regionName?: string;
  bossName?: string;
  sealLabel?: string;
  preparedSeals?: number;
  cooldownHours?: number;
}

export interface BossEntryConfirmationDialogOptions {
  onConfirm: (accessId: string) => void;
  onCancel: (accessId: string) => void;
}

export class BossEntryConfirmationDialog {
  private dialog: HTMLDialogElement | undefined;
  private accessId: string | undefined;
  private readonly options: BossEntryConfirmationDialogOptions;

  constructor(options: BossEntryConfirmationDialogOptions) {
    this.options = options;
  }

  show(confirmation: BossEntryConfirmation): void {
    if (this.accessId === confirmation.accessId && this.dialog?.open) return;
    this.close();

    const sealLabel = confirmation.sealLabel ?? "Pack Seal";
    const preparedSeals = Math.max(0, confirmation.preparedSeals ?? 0);
    const cooldownHours = Math.max(1, confirmation.cooldownHours ?? 6);
    const dialog = document.createElement("dialog");
    dialog.className = "boss-entry-dialog";
    dialog.setAttribute("aria-labelledby", "boss-entry-title");
    dialog.setAttribute("aria-describedby", "boss-entry-description");
    dialog.innerHTML = `
      <section class="boss-entry-dialog__content">
        <p class="boss-entry-dialog__eyebrow">${escapeHtml(confirmation.regionName ?? "REGIONAL BOSS")}</p>
        <h2 id="boss-entry-title">Challenge ${escapeHtml(confirmation.bossName ?? "the boss")}?</h2>
        <p id="boss-entry-description" class="boss-entry-dialog__warning">This arena is dangerous. Make sure your squad is ready before entering.</p>
        <div class="boss-entry-dialog__seal">
          <strong>${preparedSeals} ${escapeHtml(sealLabel)}${preparedSeals === 1 ? "" : "s"} ready</strong>
          <p>Entering does not consume a Seal. Defeating the boss consumes one ${escapeHtml(sealLabel)} and starts a ${cooldownHours}-hour recovery. If your squad retreats or is wiped, the Seal is preserved.</p>
        </div>
        <p class="boss-entry-dialog__status" role="status" aria-live="polite"></p>
        <div class="boss-entry-dialog__actions">
          <button type="button" class="button button-outline" data-boss-entry-cancel>Stay outside</button>
          <button type="button" class="button button-primary" data-boss-entry-confirm>Enter arena</button>
        </div>
      </section>`;

    const confirmButton = dialog.querySelector<HTMLButtonElement>(
      "[data-boss-entry-confirm]",
    );
    const cancelButton = dialog.querySelector<HTMLButtonElement>(
      "[data-boss-entry-cancel]",
    );
    const status = dialog.querySelector<HTMLElement>(
      ".boss-entry-dialog__status",
    );
    this.accessId = confirmation.accessId;
    this.dialog = dialog;
    document.body.append(dialog);

    confirmButton?.addEventListener("click", () => {
      if (!this.accessId || !confirmButton || !status) return;
      confirmButton.disabled = true;
      if (cancelButton) cancelButton.disabled = true;
      status.textContent = "Checking the entrance requirements...";
      this.options.onConfirm(this.accessId);
    });
    cancelButton?.addEventListener("click", () => this.cancel());
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      this.cancel();
    });
    dialog.addEventListener("keydown", (event) => event.stopPropagation());
    dialog.addEventListener(
      "close",
      () => {
        dialog.remove();
        if (this.dialog === dialog) {
          this.dialog = undefined;
          this.accessId = undefined;
        }
      },
      { once: true },
    );
    dialog.showModal();
  }

  close(accessId?: string): void {
    if (accessId && this.accessId !== accessId) return;
    const dialog = this.dialog;
    this.dialog = undefined;
    this.accessId = undefined;
    if (!dialog) return;
    if (dialog.open) dialog.close();
    else dialog.remove();
  }

  retry(accessId?: string): void {
    if (!this.dialog || (accessId && this.accessId !== accessId)) return;
    const confirmButton = this.dialog.querySelector<HTMLButtonElement>(
      "[data-boss-entry-confirm]",
    );
    const cancelButton = this.dialog.querySelector<HTMLButtonElement>(
      "[data-boss-entry-cancel]",
    );
    const status = this.dialog.querySelector<HTMLElement>(
      ".boss-entry-dialog__status",
    );
    if (confirmButton) confirmButton.disabled = false;
    if (cancelButton) cancelButton.disabled = false;
    if (status)
      status.textContent =
        "The entrance could not be checked. Please try again.";
  }

  dispose(): void {
    this.close();
  }

  private cancel(): void {
    const accessId = this.accessId;
    if (accessId) this.options.onCancel(accessId);
    this.close();
  }
}
